import { loginBegin, loginComplete, registerBegin, registerComplete } from '../../api/auth'
import { useAuthStore } from '../../store/auth'

interface Props {
  username: string
  email?: string
  mode: 'register' | 'login'
  deviceName?: string
  onSuccess?: () => void
  onError?: (err: string) => void
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (const byte of bytes) str += String.fromCharCode(byte)
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

function base64urlToBuffer(b64: string): ArrayBuffer {
  const str = atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
  const buf = new ArrayBuffer(str.length)
  const bytes = new Uint8Array(buf)
  for (let i = 0; i < str.length; i++) bytes[i] = str.charCodeAt(i)
  return buf
}

function encodeCredentialForServer(credential: PublicKeyCredential): unknown {
  if (credential.response instanceof AuthenticatorAttestationResponse) {
    return {
      id: credential.id,
      rawId: bufferToBase64url(credential.rawId),
      response: {
        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        attestationObject: bufferToBase64url(credential.response.attestationObject),
        transports: credential.response.getTransports(),
      },
      type: credential.type,
    }
  }
  const resp = credential.response as AuthenticatorAssertionResponse
  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      authenticatorData: bufferToBase64url(resp.authenticatorData),
      signature: bufferToBase64url(resp.signature),
      userHandle: resp.userHandle ? bufferToBase64url(resp.userHandle) : null,
    },
    type: credential.type,
  }
}

export function PasskeyButton({ username, email, mode, deviceName, onSuccess, onError }: Props) {
  const setAuth = useAuthStore((s) => s.setAuth)

  async function handleClick() {
    try {
      if (mode === 'register') {
        if (!email) throw new Error('Email required for registration')
        const options = await registerBegin(username, email) as PublicKeyCredentialCreationOptions

        // Decode challenge and user.id from base64url
        const decodedOptions = {
          ...options,
          challenge: base64urlToBuffer((options.challenge as unknown as string)),
          user: {
            ...(options.user as PublicKeyCredentialUserEntity),
            id: base64urlToBuffer((options.user as unknown as { id: string }).id),
          },
        }

        const credential = await navigator.credentials.create({ publicKey: decodedOptions }) as PublicKeyCredential
        const encoded = encodeCredentialForServer(credential)
        const result = await registerComplete(username, email ?? '', encoded, deviceName)
        setAuth(result.token, result.user_id, result.username)
        onSuccess?.()
      } else {
        const options = await loginBegin(username) as PublicKeyCredentialRequestOptions

        const decodedOptions = {
          ...options,
          challenge: base64urlToBuffer((options.challenge as unknown as string)),
          allowCredentials: ((options.allowCredentials as unknown as Array<{ id: string; type: string }>) ?? []).map(
            (c) => ({ ...c, id: base64urlToBuffer(c.id), type: 'public-key' as const })
          ),
        }

        const credential = await navigator.credentials.get({ publicKey: decodedOptions }) as PublicKeyCredential
        const encoded = encodeCredentialForServer(credential)
        const result = await loginComplete(username, encoded)
        setAuth(result.token, result.user_id, result.username)
        onSuccess?.()
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <button type="button" onClick={handleClick}>
      {mode === 'register' ? 'register with passkey' : 'login with passkey'}
    </button>
  )
}
